import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import * as React from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Typography from "@mui/material/Typography";
import { Link, useNavigate } from "react-router-dom";
import { UserContext } from "../auth/auth-context";
import { useSelectedBusiness, useSelectBusinesses } from "../../redux/store";
import { findAccountPhoto } from "./accountPhoto";
import Box from "@mui/material/Box";
import AccountCircle from "@mui/icons-material/AccountCircle";
import ButtonBase from "@mui/material/ButtonBase";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import NotificationBadge from "../reminder/notificationBadge";
import { useApolloClient } from '@apollo/client';
import { disableDeviceAlerts } from '../../util/deviceAlerts';
import { setSelectedBusiness } from "../../redux/actions/businessActions";
import { useDispatch } from "react-redux";

export const menuItemSx = {
  minHeight: 44,
  px: 1,
  py: 0.5,
  borderRadius: 2,
  alignItems: "center",
  gap: 1,
  "& .MuiListItemText-primary": { whiteSpace: "normal" },
  "&:hover": {
    backgroundColor: "action.hover",
  },
};

export const menuIconSx = {
  minWidth: 0,
  width: 28,
  height: 28,
  borderRadius: 2.2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "text.primary",
  backgroundColor: "action.hover",
  flexShrink: 0,
};

export default function AccountMenu(props: any) {
  const showNotification = props?.showNotification !== false;
  const apolloClient = useApolloClient();
  const userContext = React.useContext(UserContext);
  const dispatch = useDispatch();
  const providerUser = userContext.providerUser;
  const selectedBusiness = useSelectedBusiness();
  const businesses = useSelectBusinesses();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const accountMenuId = React.useId();

  React.useEffect(() => {
    if (providerUser?.defaultBusinessId && !selectedBusiness) {
      dispatch(setSelectedBusiness(providerUser.defaultBusinessId));
    }
  }, [providerUser?.defaultBusinessId, selectedBusiness, dispatch]);

  const accountMenuOpen = Boolean(anchorEl);

  const handleAccountMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try { await disableDeviceAlerts(apolloClient); } catch (_) { /* Logout still works offline. */ }
    userContext.logout();
    navigate("/");
    window.location.reload();
  };

  const currentStaffImage = findAccountPhoto(providerUser?.userId, selectedBusiness, businesses);

  const email = providerUser?.email;
  const accountName = providerUser?.username?.trim() || email || "Your account";

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {showNotification && providerUser?.userId && (
        <NotificationBadge email={email} />
      )}

      {/* Profile image triggers the account menu */}
      <ButtonBase
        onClick={handleAccountMenuClick}
        aria-label={`${props.menuLabel || 'Account menu'} for ${accountName}`}
        aria-controls={accountMenuOpen ? accountMenuId : undefined}
        aria-haspopup="menu"
        aria-expanded={accountMenuOpen}
        sx={{
          cursor: "pointer",
          ml: showNotification ? 0.5 : 0,
          minWidth: 44,
          height: 44,
          px: 0.75,
          gap: 0.5,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid",
          borderColor: accountMenuOpen ? "divider" : "transparent",
          backgroundColor: accountMenuOpen ? "action.selected" : "transparent",
          boxShadow: "none",
          transition: "all 180ms ease",
          "&:hover": {
            backgroundColor: "action.hover",
            transform: "translateY(-1px)",
          },
        }}
      >
        {currentStaffImage ? (
          <img
            src={currentStaffImage.url}
            alt="Profile"
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
        ) : (
          <AccountCircle
            sx={{
              fontSize: 28,
              color: "text.primary",
            }}
          />
        )}
        <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
      </ButtonBase>

      {/* Account Menu */}
      <Menu
        id={accountMenuId}
        anchorEl={anchorEl}
        open={accountMenuOpen}
        onClose={handleAccountMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          "& .MuiPaper-root": {
            display: "flex",
            flexDirection: "column",
            minWidth: 220,
            width: 240,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 96px)",
            overflowY: "auto",
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            backgroundImage: "none",
            color: "text.primary",
            boxShadow: "0 24px 50px rgba(15,31,46,0.14)",
            mt: 0.75,
            p: 0.5,
          },
        }}
      >
        <Box sx={{ px: 1, pt: 0.25, pb: 0.75 }}>
          <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", lineHeight: 1.4 }}>
            Signed in as
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "text.primary", overflowWrap: "anywhere", lineHeight: 1.4 }}>
            {accountName}
          </Typography>
          {email && email !== accountName && <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", overflowWrap: "anywhere", lineHeight: 1.4 }}>{email}</Typography>}
        </Box>
        <MenuItem
          onClick={handleAccountMenuClose}
          component={Link}
          to="/profile"
          sx={menuItemSx}
        >
          <ListItemIcon sx={menuIconSx}>
            <PersonOutlineRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Profile"
            primaryTypographyProps={{ fontSize: "0.92rem", fontWeight: 700, color: "text.primary" }}
            secondaryTypographyProps={{ fontSize: "0.77rem", color: "text.secondary" }}
          />
        </MenuItem>
        <MenuItem onClick={handleAccountMenuClose} component={Link} to="/profile?section=regional" sx={menuItemSx}>
          <ListItemIcon sx={menuIconSx}><LanguageOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Regional preferences" primaryTypographyProps={{ fontSize: '0.92rem', fontWeight: 700, color: 'text.primary' }} />
        </MenuItem>
        {providerUser?.isPlatformAdmin && <MenuItem onClick={handleAccountMenuClose} component={Link} to="/profile?section=admin" sx={menuItemSx}>
            <ListItemIcon sx={menuIconSx}><AdminPanelSettingsOutlinedIcon fontSize="small" /></ListItemIcon><ListItemText primary="Platform administration" primaryTypographyProps={{fontSize:'0.92rem',fontWeight:700,color:'text.primary'}} />
          </MenuItem>}
          <MenuItem onClick={handleAccountMenuClose} component={Link} to="/workspaces" sx={menuItemSx}>
          <ListItemIcon sx={menuIconSx}>
            <WorkspacesOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Workspaces" primaryTypographyProps={{ fontSize: "0.92rem", fontWeight: 700, color: "text.primary" }} />
        </MenuItem>
        {props.renderExtraItems?.(handleAccountMenuClose)}

        <Divider sx={{ mx: 1, my: 0.75, borderColor: 'divider' }} />
        <MenuItem
          onClick={handleLogout}
          sx={menuItemSx}
        >
          <ListItemIcon
            sx={{
              ...menuIconSx,
              color: "error.main",
              backgroundColor: "action.hover",
            }}
          >
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ fontSize: "0.92rem", fontWeight: 700, color: "error.main" }}
            secondaryTypographyProps={{ fontSize: "0.77rem", color: "text.secondary", sx: { whiteSpace: "normal", overflowWrap: "anywhere" } }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
}
